import { IncidentActivityItem, FormattedActivity } from '../Types/Incident';
import { formatTime } from './dateHelpers';

const STATUS_LABELS: Record<string, string> = {
  accounted: 'Accounted',
  notAccounted: 'Not Accounted',
  notChecked: 'Not Checked',
};

const STATUS_CHIP_TONE: Record<string, FormattedActivity['chipTone']> = {
  accounted: 'success',
  notAccounted: 'attention',
  notChecked: 'neutral',
};

const ACTIVITY_TYPE_LABELS: Record<string, string> = {
  StatusUpdate: 'Status Update',
  Checkpoint: 'Checkpoint',
  ProgressCheckpoint: 'Checkpoint',
  ManualAdd: 'Manual Add',
  PersonAdded: 'Manual Add',
  UnlistedPersonAdded: 'Manual Add',
  IncidentCreated: 'Incident Event',
  IncidentClosed: 'Incident Event',
};

const getActivityTypeLabel = (activityType: string): string =>
  ACTIVITY_TYPE_LABELS[activityType] ?? activityType.replace(/([a-z])([A-Z])/g, '$1 $2');

export const formatIncidentActivity = (item: IncidentActivityItem): FormattedActivity => {
  const actor = item.actorDisplayName || 'System';
  const person = item.personDisplayName || 'Unknown person';
  const type = item.activityType ?? '';
  const typeLabel = getActivityTypeLabel(type);
  const timeText = formatTime(item.occurredAtUtc);

  // StatusUpdate
  if (type === 'StatusUpdate' && item.effectiveStatus) {
    const status = item.effectiveStatus;
    const friendlyStatus = STATUS_LABELS[status] ?? status;
    const chipTone = STATUS_CHIP_TONE[status] ?? 'neutral';

    let primaryText: string;
    if (status === 'notChecked') {
      primaryText = `${actor} reset ${person} to Not Checked`;
    } else {
      primaryText = `${actor} marked ${person} ${friendlyStatus}`;
    }

    return {
      primaryText,
      secondaryText: `${typeLabel} · ${person}`,
      timeText,
      chipLabel: friendlyStatus,
      chipTone,
    };
  }

  // Checkpoint / ProgressCheckpoint
  if (type === 'Checkpoint' || type === 'ProgressCheckpoint') {
    return {
      primaryText: `${actor} recorded a progress checkpoint`,
      secondaryText: item.details || typeLabel,
      timeText,
    };
  }

  // ManualAdd / PersonAdded / UnlistedPersonAdded
  if (type === 'ManualAdd' || type === 'PersonAdded' || type === 'UnlistedPersonAdded') {
    return {
      primaryText: `${actor} added ${person} to the roster`,
      secondaryText: typeLabel,
      timeText,
    };
  }

  // IncidentClosed
  if (type === 'IncidentClosed') {
    return {
      primaryText: `${actor} closed the incident`,
      secondaryText: item.details || 'Incident Event',
      timeText,
    };
  }

  // IncidentCreated
  if (type === 'IncidentCreated') {
    return {
      primaryText: `${actor} created the incident`,
      secondaryText: item.details || 'Incident Event',
      timeText,
    };
  }

  // Fallback
  return {
    primaryText: `${actor} updated incident activity`,
    secondaryText: item.details || typeLabel,
    timeText,
  };
};
